import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Settings, ToggleLeft, ToggleRight, Save, DollarSign,
  MapPin, Percent, Calendar, Box, Zap
} from "lucide-react";
import { Card, GoldButton, inputClassLight as inputClass, labelClassLight as labelClass } from "./shared/UIComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOAN_TYPE_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi",
  construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};

export default function AutoInvestPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [options, setOptions] = useState({ loan_types: [], states: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const fetchPrefs = useCallback(async () => {
    try {
      const [pRes, oRes] = await Promise.all([
        axios.get(`${API}/capital/preferences`),
        axios.get(`${API}/capital/preferences/options`),
      ]);
      setPrefs(pRes.data);
      setOptions(oRes.data);
      setForm({
        auto_invest_enabled: pRes.data.auto_invest_enabled || false,
        reinvest_distributions: pRes.data.reinvest_distributions || false,
        max_per_deal: pRes.data.max_per_deal || "",
        preferred_loan_types: pRes.data.preferred_loan_types || [],
        preferred_states: pRes.data.preferred_states || [],
        min_deal_size: pRes.data.min_deal_size || "",
        max_deal_size: pRes.data.max_deal_size || "",
        min_yield: pRes.data.min_yield || "",
        max_term_months: pRes.data.max_term_months || "",
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { auto_invest_enabled: form.auto_invest_enabled, reinvest_distributions: form.reinvest_distributions || false };
      if (form.max_per_deal) payload.max_per_deal = parseFloat(form.max_per_deal);
      if (form.preferred_loan_types.length > 0) payload.preferred_loan_types = form.preferred_loan_types;
      if (form.preferred_states.length > 0) payload.preferred_states = form.preferred_states;
      if (form.min_deal_size) payload.min_deal_size = parseFloat(form.min_deal_size);
      if (form.max_deal_size) payload.max_deal_size = parseFloat(form.max_deal_size);
      if (form.min_yield) payload.min_yield = parseFloat(form.min_yield);
      if (form.max_term_months) payload.max_term_months = parseInt(form.max_term_months);
      await axios.put(`${API}/capital/preferences`, payload);
      fetchPrefs();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const toggleType = (t) => setForm(f => ({
    ...f, preferred_loan_types: f.preferred_loan_types.includes(t) ? f.preferred_loan_types.filter(x => x !== t) : [...f.preferred_loan_types, t],
  }));

  const toggleState = (s) => setForm(f => ({
    ...f, preferred_states: f.preferred_states.includes(s) ? f.preferred_states.filter(x => x !== s) : [...f.preferred_states, s],
  }));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;

  return (
    <div className="space-y-6" data-testid="auto-invest-preferences">
      {/* Auto-Invest Toggle */}
      <Card hover={false}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${form.auto_invest_enabled ? "bg-emerald-100 border border-emerald-200" : "bg-gray-100 border border-gray-200"}`}>
              <Zap className={`h-5 w-5 ${form.auto_invest_enabled ? "text-emerald-700" : "text-gray-400"}`} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#0A0A0A]">Auto-Invest</h3>
              <p className="text-sm text-[#888]">Automatically allocate capital to deals matching your criteria</p>
            </div>
          </div>
          <button onClick={() => setForm(f => ({ ...f, auto_invest_enabled: !f.auto_invest_enabled }))}
            className="p-2" data-testid="auto-invest-toggle">
            {form.auto_invest_enabled
              ? <ToggleRight className="h-8 w-8 text-emerald-600" />
              : <ToggleLeft className="h-8 w-8 text-gray-400" />}
          </button>
        </div>
      </Card>

      {/* Capital Flywheel — Reinvest Distributions */}
      <Card hover={false}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${form.reinvest_distributions ? "bg-gold/20 border border-gold/30" : "bg-gray-100 border border-gray-200"}`}>
              <DollarSign className={`h-5 w-5 ${form.reinvest_distributions ? "text-gold-dark" : "text-gray-400"}`} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-[#0A0A0A]">Reinvest Distributions</h3>
              <p className="text-xs text-[#888]">Automatically reinvest earned distributions into new opportunities — compound your returns</p>
            </div>
          </div>
          <button onClick={() => setForm(f => ({ ...f, reinvest_distributions: !f.reinvest_distributions }))}
            className="p-2" data-testid="reinvest-toggle">
            {form.reinvest_distributions
              ? <ToggleRight className="h-7 w-7 text-gold-dark" />
              : <ToggleLeft className="h-7 w-7 text-gray-400" />}
          </button>
        </div>
        {form.reinvest_distributions && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 p-3 rounded-xl bg-gold/5 border border-gold/10">
            <div className="text-[10px] text-gold-dark font-medium">Capital Flywheel Active</div>
            <div className="text-[10px] text-[#888] mt-0.5">When distributions are received, they will be automatically deployed into the next matching opportunity based on your criteria above.</div>
          </motion.div>
        )}
      </Card>

      <AnimatePresence>
        {form.auto_invest_enabled && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-5">
            {/* Max Per Deal */}
            <Card hover={false}>
              <label className="block">
                <span className={labelClass}>Maximum Investment Per Deal ($)</span>
                <input type="number" value={form.max_per_deal} onChange={e => setForm(f => ({ ...f, max_per_deal: e.target.value }))}
                  className={inputClass} placeholder="e.g., 200000" data-testid="pref-max-per-deal" />
              </label>
            </Card>

            {/* Loan Types */}
            <Card hover={false}>
              <span className={labelClass}>Preferred Loan Types</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {options.loan_types.map(t => (
                  <button key={t} onClick={() => toggleType(t)} data-testid={`pref-type-${t}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.preferred_loan_types.includes(t) ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-cream border-cream-warm text-[#666] hover:border-emerald-200"}`}>
                    {LOAN_TYPE_LABELS[t] || t}
                  </button>
                ))}
              </div>
              {form.preferred_loan_types.length === 0 && <p className="text-[10px] text-[#999] mt-1">Leave empty to accept all loan types</p>}
            </Card>

            {/* States */}
            <Card hover={false}>
              <span className={labelClass}>Preferred States</span>
              <div className="flex flex-wrap gap-1.5 mt-2 max-h-28 overflow-y-auto p-2 rounded-xl border border-cream-warm bg-cream/30">
                {options.states.map(s => (
                  <button key={s} onClick={() => toggleState(s)} data-testid={`pref-state-${s}`}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${form.preferred_states.includes(s) ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-cream-warm text-[#666] hover:border-emerald-200"}`}>
                    {s}
                  </button>
                ))}
              </div>
              {form.preferred_states.length > 0 && <p className="text-[10px] text-emerald-600 mt-1">{form.preferred_states.length} states selected</p>}
            </Card>

            {/* Financial Criteria */}
            <Card hover={false}>
              <span className={labelClass}>Financial Criteria</span>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <label className="block">
                  <span className="text-xs text-[#888]">Min Deal Size ($)</span>
                  <input type="number" value={form.min_deal_size} onChange={e => setForm(f => ({ ...f, min_deal_size: e.target.value }))}
                    className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 100000" data-testid="pref-min-deal" />
                </label>
                <label className="block">
                  <span className="text-xs text-[#888]">Max Deal Size ($)</span>
                  <input type="number" value={form.max_deal_size} onChange={e => setForm(f => ({ ...f, max_deal_size: e.target.value }))}
                    className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 2000000" data-testid="pref-max-deal" />
                </label>
                <label className="block">
                  <span className="text-xs text-[#888]">Min Yield (%)</span>
                  <input type="number" step="0.1" value={form.min_yield} onChange={e => setForm(f => ({ ...f, min_yield: e.target.value }))}
                    className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 8.0" data-testid="pref-min-yield" />
                </label>
                <label className="block">
                  <span className="text-xs text-[#888]">Max Term (months)</span>
                  <input type="number" value={form.max_term_months} onChange={e => setForm(f => ({ ...f, max_term_months: e.target.value }))}
                    className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 18" data-testid="pref-max-term" />
                </label>
              </div>
            </Card>

            {/* Save */}
            <GoldButton onClick={handleSave} disabled={saving} data-testid="save-preferences-btn">
              {saving ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Saving...</span>
                : <span className="flex items-center gap-2"><Save className="h-4 w-4" />Save Preferences</span>}
            </GoldButton>
          </motion.div>
        )}
      </AnimatePresence>

      {!form.auto_invest_enabled && (
        <Card hover={false}>
          <div className="text-center py-6">
            <Settings className="h-10 w-10 text-emerald-200 mx-auto mb-3" />
            <p className="text-sm text-[#666]">Enable Auto-Invest to set your capital deployment preferences.</p>
            <p className="text-xs text-[#999] mt-1">Deals matching your criteria will be automatically allocated capital from your wallet.</p>
          </div>
        </Card>
      )}
    </div>
  );
}
